import { useMemo } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'

/**
 * DocumentContributor document interface
 *
 * A user who has contributed to a Document (page).
 * Auto-created server-side on any Documents push or DocumentContent push.
 */
export interface DocumentContributorDocument extends BaseDocument {
  documentId: string
  userId: string
}

const hooks = createEntityHooks<DocumentContributorDocument>('documentcontributors')

/**
 * Get the contributor user IDs for a specific document.
 * Returns deduplicated list (the unique index keeps the DB clean, but a
 * client-side dedupe guards against transient duplicate rows during sync).
 */
export const useDocumentContributors = (documentId: string | undefined) => {
  const { data, isLoading } = hooks.useEntities(
    documentId ? { documentId, isDeleted: false } : undefined,
    { enabled: !!documentId },
  )

  const contributorUserIds = useMemo<Array<string>>(() => {
    if (!documentId) return []
    return Array.from(new Set(data.map((c) => c.userId)))
  }, [documentId, data])

  return { contributorUserIds, contributors: data, isLoading }
}
